import React, { useEffect, useState } from "react";
import { Box, Button, Group, Modal, NumberInput, Text } from "@mantine/core";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import surfaceStyles from "../../common/surface/surface.module.css";

interface CashOverageModalProps {
  opened: boolean;
  onClose: () => void;
  /** Cash received from the customer. */
  tendered: number;
  /** Balance due on the transaction. */
  balanceDue: number;
  /** Confirms the split; `tip` is the overage portion kept as tip. */
  onConfirm: (tip: number) => void;
}

/**
 * Shown only when the cash received exceeds the balance due: asks how much
 * of the overage the staff keeps as tip; the rest is returned as change.
 */
export default function CashOverageModal({
  opened,
  onClose,
  tendered,
  balanceDue,
  onConfirm,
}: CashOverageModalProps) {
  const overage = Math.max(0, Math.round((tendered - balanceDue) * 100) / 100);
  const [tip, setTip] = useState<number | string>(overage);

  useEffect(() => {
    if (opened) setTip(overage);
  }, [opened, overage]);

  const tipNumber = typeof tip === "number" ? tip : parseFloat(tip);
  const safeTip = Number.isNaN(tipNumber) ? 0 : tipNumber;
  const isValid = safeTip >= 0 && safeTip <= overage;
  const change = Math.max(0, Math.round((overage - safeTip) * 100) / 100);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Cash exceeds balance due"
      size={380}
      radius={18}
      centered
    >
      <Text size="sm" c="dimmed" mb="md" lh={1.5}>
        The cash received ({formatCurrency(tendered)}) is more than the balance
        due ({formatCurrency(balanceDue)}). How much of the extra{" "}
        {formatCurrency(overage)} is the staff keeping as tip?
      </Text>

      <NumberInput
        label="Tip kept"
        value={tip}
        onChange={setTip}
        prefix="$"
        min={0}
        max={overage}
        decimalScale={2}
        hideControls
        data-autofocus
        error={
          !isValid
            ? `Tip must be between $0 and ${formatCurrency(overage)}`
            : undefined
        }
      />

      <Group justify="space-between" mt={12}>
        <Text size="xs" c="dimmed">
          Change returned {formatCurrency(change)}
        </Text>
      </Group>

      <Box mt={18}>
        <Button
          fullWidth
          classNames={{ root: surfaceStyles.primaryAction }}
          onClick={() => onConfirm(safeTip)}
          disabled={!isValid}
        >
          Record cash payment
        </Button>
      </Box>
    </Modal>
  );
}
